/**
 * <head> metadata for marketing pages: string form for the prerender, DOM form for client-side
 * navigation. Pure apart from `applyHead`.
 *
 * @typedef {{ path: string, canonicalPath?: string, title: string, description: string, body: string, ogImage?: string, ogType?: string, jsonLd?: object[] }} PageView
 */
import { SITE } from './data.js';
import { esc } from './ui.js';

export const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT@0,9..144,300..700,0..100;1,9..144,300..700,0..100&family=Inter:wght@400..700&display=swap';

/** @param {PageView} v */
const meta = (v) => {
  const url = SITE.origin + (v.canonicalPath ?? v.path);
  const image = SITE.origin + (v.ogImage ?? '/og/og.png');
  return {
    url,
    image,
    props: [
      ['og:type', v.ogType ?? 'website'],
      ['og:site_name', SITE.name],
      ['og:title', v.title],
      ['og:description', v.description],
      ['og:url', url],
      ['og:image', image],
      ['og:image:width', '1200'],
      ['og:image:height', '630'],
      ['og:image:alt', `${v.title}`],
    ],
    names: [
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', v.title],
      ['twitter:description', v.description],
      ['twitter:image', image],
    ],
  };
};

/**
 * Head tags (minus <title>/<meta description>, which the prerender swaps in place).
 * @param {PageView} v
 */
export function headTags(v) {
  const m = meta(v);
  const tags = [
    `<link rel="canonical" href="${esc(m.url)}" />`,
    ...m.props.map(([p, c]) => `<meta property="${p}" content="${esc(c)}" />`),
    ...m.names.map(([n, c]) => `<meta name="${n}" content="${esc(c)}" />`),
  ];
  for (const ld of v.jsonLd ?? []) {
    tags.push(
      `<script type="application/ld+json">${JSON.stringify(ld).replace(/</g, '\\u003c')}</script>`,
    );
  }
  return tags.join('\n    ');
}

/** Client-side: keep title/description/canonical/OG in sync after SPA navigation. */
/** @param {PageView} v */
export function applyHead(v) {
  const m = meta(v);
  document.title = v.title;
  /** @param {string} sel @param {() => HTMLElement} make */
  const upsert = (sel, make) =>
    document.head.querySelector(sel) ?? document.head.appendChild(make());
  const setMeta = (attr, key, content) => {
    upsert(`meta[${attr}="${key}"]`, () => {
      const el = document.createElement('meta');
      el.setAttribute(attr, key);
      return el;
    }).setAttribute('content', content);
  };
  setMeta('name', 'description', v.description);
  for (const [p, c] of m.props) setMeta('property', p, c);
  for (const [n, c] of m.names) setMeta('name', n, c);
  upsert('link[rel="canonical"]', () => {
    const el = document.createElement('link');
    el.rel = 'canonical';
    return el;
  }).setAttribute('href', m.url);
  for (const s of document.head.querySelectorAll('script[type="application/ld+json"]')) s.remove();
  for (const ld of v.jsonLd ?? []) {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.textContent = JSON.stringify(ld);
    document.head.appendChild(s);
  }
}

/** Shared JSON-LD bits. */
export const LD = {
  book: {
    '@type': 'Book',
    name: 'Fair Play',
    author: { '@type': 'Person', name: 'Eve Rodsky', url: SITE.eveUrl },
    url: SITE.bookUrl,
  },
  /** @param {[string, string][]} trail */
  breadcrumbs: (trail) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: SITE.origin + path,
    })),
  }),
};
